import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useState } from 'react'

import DialogDownloadForm from '@/components/DialogDownloadForm'
import EmptyState from '@/components/EmptyState'
import SelectComponent from '@/components/SelectComponent'
import { DownloadFormType } from '@/config/enum'
import { appSetIsLoading } from '@/reducers/app'
import { reportUtil, useLazyGetReportListQuery, usePostReportDownloadMutation } from '@/services/report'
import { Report, ReportDownloadRequest } from '@/types/report'
import { generateArrayObjects } from '@/utils/form'
import { useAppDispatch } from '@/utils/hooks'

import ReportCard from './components/ReportCard'
import Pagination from '../../media/components/Pagination'

import EmptyStateImage from '~/assets/images/jdih/jdih-empty-state.webp'
import NotFoundStateImage from '~/assets/images/jdih/jdih-not-found-state.webp'

const ReportContent: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [getReportList, { data, isSuccess }] = useLazyGetReportListQuery()

  const [initialDataCount, setInitialDataCount] = useState(0)
  const [limit, _setLimit] = useState(12)
  const [skip, setSkip] = useState(0)
  const [page, setPage] = useState(0)
  const [filterYear, setFilterYear] = useState<string | null>(null)
  const [selectedReport, setSelectedReport] = useState<Report>()

  const [postReportDownload] = usePostReportDownloadMutation()

  const years = [{ label: t('common:year'), value: '' }].concat(generateArrayObjects())

  const handleResetFilterPagination = async () => {
    _setLimit(12)
    setSkip(0)
    setPage(0)
  }

  const handleChangeYear = useCallback(async (value: string) => {
    setFilterYear(value || null)
    await handleResetFilterPagination()
  }, [])

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * limit) % Number(data?.data.metadata.total)
    setPage(event.selected)
    setSkip(newOffset)
  }

  const handleResetFilter = async () => {
    setFilterYear(null)
    await handleResetFilterPagination()
  }

  const handleClickDownload = (xid: string) => {
    setSelectedReport(data?.data.items.find((r) => r.xid == xid))
  }

  const handleDownload = (body: ReportDownloadRequest['body']) => {
    if (selectedReport) {
      postReportDownload({
        xid: selectedReport.xid,
        body,
      })
        .unwrap()
        .then((res) => {
          if (res.success) {
            dispatch(appSetIsLoading(true))
            const url = selectedReport.file.url
            fetch(url).then((res) => {
              const fileName = new URL(url).pathname.split('/').pop()

              res.blob().then((b) => {
                const a = document.createElement('a')
                a.href = URL.createObjectURL(b)
                a.setAttribute('download', String(fileName))
                a.click()

                dispatch(appSetIsLoading(false))
              })
            })

            setSelectedReport(undefined)
          }
        })
    }
  }

  useEffect(() => {
    getReportList({
      params: { skip: skip, limit: limit, year: filterYear || '' },
      headers: { 'Accept-Language': String(router.locale) },
    })

    return () => {
      reportUtil.resetApiState()
    }
  }, [filterYear, limit, skip])

  useEffect(() => {
    if (isSuccess && data) {
      setInitialDataCount(data.data.items.length)
    }
  }, [isSuccess])

  return (
    <>
      <div className='container flex flex-col gap-6 p-4 pb-8 lg:gap-8 lg:py-8'>
        {initialDataCount > 0 && (
          <>
            <div className='flex flex-col justify-between gap-3 lg:flex-row lg:items-center'>
              <h5 className='font-bold leading-normal text-dark lg:text-2xl'>{t('report:title')}</h5>

              <div className='z-20 flex items-center gap-3'>
                <div className='h-11 w-full lg:w-40'>
                  <SelectComponent
                    options={years}
                    selected={filterYear}
                    placeholder={t('report:content.selectYearPlaceholder') as string}
                    onChangeSelected={handleChangeYear}
                  />
                </div>

                <button
                  type='button'
                  className='flex h-11 items-center justify-center rounded-full bg-red px-6 py-3 font-bold text-white disabled:bg-action-disabled disabled:text-form-disable-background'
                  onClick={handleResetFilter}
                  disabled={filterYear == null}
                >
                  {t('common:reset')}
                </button>
              </div>
            </div>

            {isSuccess && data && data.data.items.length > 0 && (
              <>
                <div className='grid grid-cols-1 gap-y-6 lg:grid-cols-4 lg:gap-x-7 lg:gap-y-7.5'>
                  {data.data.items.map((report) => (
                    <ReportCard
                      key={report.xid}
                      report={report}
                      onClickDownload={handleClickDownload}
                    />
                  ))}
                </div>

                <Pagination
                  onPageChange={handlePageClick}
                  selected={page}
                  perPage={limit}
                  totalData={data.data.metadata.total ?? 0}
                />
              </>
            )}

            {isSuccess && data && data.data.items.length == 0 && (
              <EmptyState
                img={NotFoundStateImage.src}
                title={t('report:content.notFoundState.title')}
                description={t('report:content.notFoundState.description')}
                imgClass='mx-auto w-[250px] h-[200px] lg:w-[500px] lg:h-[400px]'
                containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px] items-center'
                contentWidth='w-[250px] lg:w-full'
                buttonText={t('common:backTo', { page: t('report:title') }) as string}
                onClick={handleResetFilter}
              />
            )}
          </>
        )}

        {isSuccess && initialDataCount == 0 && (
          <EmptyState
            img={EmptyStateImage.src}
            title={t('report:content.emptyState.title')}
            description={t('report:content.emptyState.description')}
            imgClass='mx-auto w-[250px] h-[200px] lg:w-[500px] lg:h-[400px]'
            containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px] items-center'
            contentWidth='w-[250px] lg:w-full'
          />
        )}
      </div>

      <DialogDownloadForm
        type={DownloadFormType.Report}
        title={selectedReport?.title ?? ''}
        isOpen={selectedReport != undefined}
        onClose={() => setSelectedReport(undefined)}
        onDownload={handleDownload}
      />
    </>
  )
}

export default ReportContent
